'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/hooks/use-toast'
import { useApiFetch } from '@/features/auth'
import {
  createOwnBuildPortfolioCase,
  setOwnConsultationSlot,
  type CreateOwnBuildPortfolioCaseInput,
} from '@/features/cases/api/cases.api'
import type { CaseDetail, CaseDocumentKind, ServiceType } from '@/features/cases/types'
import { useFileUpload } from '@/lib/upload/useFileUpload'
import { AppError } from '@/lib/errors/appError'
import { queryKeys } from '@/lib/queryKeys'

function toastError(error: unknown) {
  toast({ variant: 'destructive', title: AppError.fromUnknown(error).message })
}

export interface CreateOwnCaseInput {
  serviceType: ServiceType
  consultationSlotAt: string
  kind: CaseDocumentKind
  file: File
  password?: string
}

/**
 * The investor self-service "request an additional service" flow's single submission —
 * `POST /cases/mine`, multipart. The document and the consultation slot are required together
 * with `serviceType` so the case is only ever created fully formed on the backend (no case with a
 * missing document, no empty case left behind if the file fails validation) — matches
 * `CasesService.createOwn`'s own "validate/store the file before the transaction" shape. Uses the
 * XHR-based `useFileUpload` (not a plain `authedFetch` JSON call) since the request carries a
 * file, same reasoning as `useUploadCaseDocument`.
 */
export function useCreateOwnCase() {
  const { upload } = useFileUpload()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateOwnCaseInput) => {
      const formData = new FormData()
      formData.append('serviceType', input.serviceType)
      formData.append('consultationSlotAt', input.consultationSlotAt)
      formData.append('kind', input.kind)
      if (input.password) formData.append('password', input.password)
      formData.append('file', input.file)
      return upload<CaseDetail>('/api/v1/cases/mine', formData)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.investorsResource.myCases })
    },
    onError: toastError,
  })
}

/**
 * The logged-in investor's self-service Build My Portfolio submission — `POST
 * /cases/mine/build-portfolio`, a plain JSON `authedFetch` call (no document, unlike
 * `useCreateOwnCase`'s multipart upload), so this doesn't need `useFileUpload`.
 */
export function useCreateOwnBuildPortfolioCase() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateOwnBuildPortfolioCaseInput) =>
      createOwnBuildPortfolioCase(authedFetch, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.investorsResource.myCases })
    },
    onError: toastError,
  })
}

/** Re-picking a slot on an already-existing self-service case — the initial slot is submitted
 * together with `useCreateOwnCase` itself. */
export function useSetOwnConsultationSlot(caseId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (consultationSlotAt: string) =>
      setOwnConsultationSlot(authedFetch, caseId, consultationSlotAt),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.investorsResource.myCases })
      void queryClient.invalidateQueries({ queryKey: queryKeys.cases.real(caseId) })
    },
    onError: toastError,
  })
}
